import { useLocation } from "react-router-dom";
import { useMemo } from "react";

export interface PreservedSearchState {
  searchQuery: string;
}

// Ensures the query string starts with a question mark
const ensureLeadingQuestionMark = (value: string) => {
  if (!value) {
    return "";
  }

  return value.startsWith("?") ? value : `?${value}`;
};

// Hook to preserve and retrieve search query parameters across navigation
export const usePreservedSearchQuery = () => {
  // Get current location from React Router
  const location = useLocation();

  // Extract search query from location state, defaulting to empty string
  const stateSearchQuery = (
    (location.state as Partial<PreservedSearchState> | null)?.searchQuery ?? ""
  ).trim();

  // Get search string from URL (e.g., "?foo=bar")
  const locationSearch = location.search ?? "";

  // Use state query if available, otherwise fall back to URL search string
  const preservedSearchRaw = stateSearchQuery || locationSearch;
  // Normalize the search string with leading question mark
  const preservedSearch = ensureLeadingQuestionMark(preservedSearchRaw);

  // Memoize state object for link navigation to preserve search context
  const linkState = useMemo<PreservedSearchState | undefined>(() => {
    return preservedSearch ? { searchQuery: preservedSearch } : undefined;
  }, [preservedSearch]);

  return {
    preservedSearch,
    linkState,
  };
};
